import { BHASHINI_SCRIPT_URL } from "./constants";

const skip = 'script,style,noscript,textarea,[translate="no"],[contenteditable="true"],.dont-translate,.bhashini-skip-translation,.bhashini-plugin-container,#bhashini-translation,#bhashiniLanguageDropdown';
const attributes = ["placeholder", "title", "aria-label", "alt"];

// Compact dotted translated initials, including adjacent initials joined by the service.
const normalizeTranslation = (text, source) => {
  const lengths = new Set((source.match(/\b[A-Z]{2,}\b/g) || []).map((acronym) => acronym.length));
  if (!lengths.size) return text;
  return text.replace(/(^|[^\p{L}\p{M}])((?:[\p{L}\p{M}]{1,8}\.\s*){2,})/gu, (match, prefix, initials) => {
    const parts = initials.trim().split(/\.\s*/u).filter(Boolean);
    // Adjacent acronyms (for example an organisation name followed by AI)
    // arrive as one dotted run. Preserve the boundary between source acronyms.
    const split = (remaining) => {
      if (!remaining) return [];
      for (const length of [...lengths].sort((a, b) => b - a)) {
        if (length > remaining) continue;
        const rest = split(remaining - length);
        if (rest) return [length, ...rest];
      }
      return null;
    };
    const groups = split(parts.length);
    if (groups) {
      let offset = 0;
      const compact = groups.map((length) => {
        const group = parts.slice(offset, offset + length).join("");
        offset += length;
        return group;
      });
      return prefix + compact.join(" ") + (initials.match(/\s*$/u)?.[0] || "");
    }
    if (![...lengths].some((length) => parts.length <= length && parts.length >= Math.ceil(length / 2))) return match;
    return prefix + parts.join("") + (initials.match(/\s*$/u)?.[0] || "");
  });
};

// One owner for source text and DOM writes. Vendor observers and application
// observers must not translate the same nodes independently.
export function createSiteTranslator(root, request) {
  const records = new Map();
  const cache = new Map();
  let language = "en";
  let generation = 0;
  let abort;
  let timer;
  let running = false;
  let rescanNeeded = false;
  let ready = false;
  let stopped = false;
  const read = (item) => item.attribute ? item.node.getAttribute(item.attribute) : item.node.nodeValue;
  const write = (item, value) => {
    if (item.attribute) item.node.setAttribute(item.attribute, value);
    else item.node.nodeValue = value;
    item.last = value;
  };
  const remember = (node, attribute = "") => {
    let entries = records.get(node);
    if (!entries) records.set(node, entries = new Map());
    const value = attribute ? node.getAttribute(attribute) : node.nodeValue;
    let item = entries.get(attribute);
    if (!item) {
      item = { node, attribute, source: value, last: value, language: "en" };
      entries.set(attribute, item);
    } else if (value !== item.last) {
      // React updated this node with new source content (carousel/API/route).
      item.source = value;
      item.last = value;
      item.language = "en";
    }
    return item;
  };
  const collect = () => {
    for (const node of records.keys()) if (!root.contains(node)) records.delete(node);
    const items = [];
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    while (walker.nextNode()) {
      const node = walker.currentNode;
      if (node.nodeValue.trim() && !node.parentElement?.closest(skip)) items.push(remember(node));
    }
    root.querySelectorAll(attributes.map((name) => `[${name}]`).join(",")).forEach((node) => {
      if (node.closest(skip)) return;
      attributes.forEach((attribute) => {
        if (node.getAttribute(attribute)?.trim()) items.push(remember(node, attribute));
      });
    });
    return items;
  };
  const schedule = () => {
    if (stopped) return;
    clearTimeout(timer);
    timer = setTimeout(scan, 120);
  };
  const scan = async () => {
    if (stopped) return;
    const items = collect();
    if (language === "en" || !ready) return;
    if (running) { rescanNeeded = true; return; }
    const version = generation;
    const target = language;
    const pending = items.filter((item) => item.language !== target && /\p{L}/u.test(item.source || ""));
    if (!pending.length) {
      window.dispatchEvent(new CustomEvent("bhashini-translation-status", { detail: { loading: false } }));
      return;
    }
    running = true;
    const controller = new AbortController();
    abort = controller;
    window.dispatchEvent(new CustomEvent("bhashini-translation-status", { detail: { loading: true } }));
    let failed = false;
    try {
      const sources = [...new Set(pending.map((item) => item.source))];
      let offset = 0;
      const worker = async () => {
      while (offset < sources.length && version === generation && !stopped) {
        const batch = sources.slice(offset, offset + 40);
        offset += 40;
        const missing = batch.filter((source) => !cache.has(`${target}\0${source}`));
        if (missing.length) {
          const output = await request(missing, target, controller.signal);
          if (version !== generation || stopped) return;
          if (!Array.isArray(output) || output.length !== missing.length || output.some((entry) => typeof entry?.target !== "string")) {
            throw new Error("Bhashini returned an invalid translation response");
          }
          missing.forEach((source, index) => cache.set(`${target}\0${source}`, normalizeTranslation(output[index].target, source)));
        }
        if (version !== generation || stopped) return;
        pending.forEach((item) => {
          const translated = cache.get(`${target}\0${item.source}`);
          if (translated !== undefined && root.contains(item.node) && read(item) === item.last) {
            write(item, translated);
            item.language = target;
          }
        });
      }
      };
      await Promise.all([worker(), worker(), worker()]);
    } catch (error) {
      if (error.name !== "AbortError" && version === generation) {
        failed = true;
        controller.abort();
        console.warn("Bhashini translation failed", error);
        window.dispatchEvent(new CustomEvent("bhashini-translation-error", { detail: { message: error.message } }));
      }
    } finally {
      if (version === generation) {
        running = false;
        window.dispatchEvent(new CustomEvent("bhashini-translation-status", { detail: { loading: false, failed } }));
        if (rescanNeeded && !failed) { rescanNeeded = false; schedule(); }
      }
    }
  };
  const observer = new MutationObserver((mutations) => {
    const external = mutations.some((mutation) => {
      if (mutation.type === "childList") return true;
      const item = records.get(mutation.target)?.get(mutation.type === "characterData" ? "" : mutation.attributeName);
      return !item || read(item) !== item.last;
    });
    if (external) schedule();
  });
  observer.observe(root, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: attributes });
  window.addEventListener("scroll", schedule, { capture: true, passive: true });
  collect();
  return {
    scan,
    enable() { ready = true; schedule(); },
    select(next) {
      if (language === next) { schedule(); return; }
      generation++;
      abort?.abort();
      running = false;
      collect();
      records.forEach((entries) => entries.forEach((item) => {
        if (root.contains(item.node)) write(item, item.source);
        item.language = "en";
      }));
      language = next;
      window.dispatchEvent(new CustomEvent("bhashini-translation-status", { detail: { loading: next !== "en" } }));
      schedule();
    },
    stop() {
      stopped = true;
      generation++;
      abort?.abort();
      clearTimeout(timer);
      observer.disconnect();
      window.removeEventListener("scroll", schedule, true);
    },
  };
}

let translator;
export function startSiteTranslation() {
  if (!translator) translator = createSiteTranslator(document.body, async (texts, language, signal) => {
    const response = await fetch(`${new URL(BHASHINI_SCRIPT_URL, window.location.href).origin}/v2/translate-text`, {
      method: "POST", signal,
      headers: { "Content-Type": "application/json", "X-Utility-Version": "v3" },
      body: JSON.stringify({ sourceLanguage: "en", targetLanguage: language, textData: texts }),
    });
    if (!response.ok) throw new Error(`Bhashini request failed (${response.status})`);
    return response.json();
  });
  return translator;
}

export function connectSiteTranslation() {
  // Retain the Bhashini picker, but disable its competing DOM writers, including
  // already scheduled startup rescans. Application requests use its v3 endpoint.
  window.selectedTargetLanguageCode = null;
  window.observer?.disconnect();
  window.translationObserver?.disconnect();
  window.clearTimeout(window.translationDebounceTimer);
  window.clearTimeout(window.debounceTimer);
  window.translationQueue = [];
  window.nodesToTranslate = [];
  ["translateAllTextNodes", "translateElementText", "translateElementTextNodes", "translateNodesImmediately", "processTranslationBatch", "scheduleInitialTranslationRescan"].forEach((name) => {
    window[name] = () => Promise.resolve();
  });
  startSiteTranslation().enable();
}

export function changeSiteLanguage(language) {
  startSiteTranslation().select(language);
}
